import React, { lazy, useEffect } from "react";
import { Navigate } from "react-router";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  Outlet,
} from "react-router-dom";

import SignUpModal from "../components/SignUpModal";
import SignInModal from "../components/SignInModal";
import BlockedUserModal from "../components/BlockedUserModal";
import AddEmailModal from "../components/AddEmailModal";
import ResetPasswordModal from "../components/ForgotPasswordModal";
import TwoFactorsAuthModal from "../components/TwoFactorsAuthModal";
import ScrollHelper from "../components/ScrollHelper";
import TxModal from "../components/TxModal";
import ViewPostBySubscriptionModal from "../components/ViewPostBySubscriptionModal";
import CustomToastContainer from "../components/Toast";
import AuthRedirects from "../pages/AuthRedirects";
import CookiesBanner from "../components/CookiesBanner";
import ReferralHandler from "../components/ReferralHandler";

import { StorageService } from "@services/StorageService";
import { useService } from "@/base/hooks/useService";
import {
  initGoogleAnalytic,
  resetGoogleAnalytic,
} from "@helpers/googleAnalytic";

const Audience = lazy(() => import("@pages/Livestream/pages/Audience"));
const Host = lazy(() => import("../pages/Livestream/pages/Host"));
const Application = lazy(() => import("../pages/Application"));
const AdultsPrivacyPolicy = lazy(() => import("../pages/AdultsPrivacyPolicy"));
const Logout = lazy(() => import("../Logout"));
const NotFound = lazy(() => import("../pages/NotFound"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));
const Landing = lazy(() => import("../pages/Landing"));
const StandardContract = lazy(() => import("../pages/StandardContract"));
const HowItWorks = lazy(() => import("../pages/HowItWorks"));
const TermsOfUse = lazy(() => import("../pages/TermsOfUse"));
const ContactUs = lazy(() => import("../pages/ContactUs"));
const CookiePolicy = lazy(() => import("../pages/CookiePolicy"));
const CreatorProfile = lazy(() => import("../pages/CreatorProfile"));
const AboutUs = lazy(() => import("../pages/AboutUs"));
const TermsOfRequests = lazy(() => import("../pages/TermsOfRequests"));
const ContentRequestCompletePayment = lazy(
  () => import("../pages/ContentRequestCompletePayment"),
);
const ContentRequestCompletePaymentSuccess = lazy(
  () => import("../pages/ContentRequestCompletePaymentSuccess"),
);

import routes from "@const/routes";
import { localstorageKeys } from "@const/store";

const useAppRoutes = () => {
  const storageService = useService(StorageService);

  const onFunctionalCookiesChange = () => {
    const functionalCookies = storageService.get(
      localstorageKeys.functionalCookies,
      false,
    );
    if (!functionalCookies) {
      storageService.remove(localstorageKeys.functionalCookies);
    }
  };

  const onMarketingCookiesChange = () => {
    const marketingCookies = storageService.get(
      localstorageKeys.marketingCookies,
      false,
    );
    if (!marketingCookies) {
      resetGoogleAnalytic();
      storageService.remove(localstorageKeys.marketingCookies);
    } else {
      initGoogleAnalytic();
    }
  };

  useEffect(() => {
    storageService.onChange(
      localstorageKeys.marketingCookies,
      onMarketingCookiesChange,
    );
  }, [storageService, onMarketingCookiesChange]);

  useEffect(() => {
    storageService.onChange(
      localstorageKeys.functionalCookies,
      onFunctionalCookiesChange,
    );
  }, [storageService, onFunctionalCookiesChange]);

  return createBrowserRouter(
    createRoutesFromElements(
      <Route
        element={
          <>
            <SignUpModal />
            <SignInModal />
            <BlockedUserModal />
            <AddEmailModal />
            <ResetPasswordModal />
            <TwoFactorsAuthModal />
            <ScrollHelper />
            <TxModal />
            <ViewPostBySubscriptionModal />
            <CustomToastContainer />
            <AuthRedirects />
            <ReferralHandler />
            <CookiesBanner />

            {/* Outlet renders all routes which we defined below */}
            <Outlet />
          </>
        }
      >
        <Route path={`${routes.APP()}*`} element={<Application />} />
        <Route path={routes.LOGOUT()} element={<Logout />} />
        <Route path={routes.NOT_FOUND()} element={<NotFound />} />
        <Route path={routes.PRIVACY_POLICY()} element={<PrivacyPolicy />} />
        <Route path={routes.HOW_IT_WORKS()} element={<HowItWorks />} />
        <Route
          path={routes.ADULT_PRIVACY_POLICY()}
          element={<AdultsPrivacyPolicy />}
        />
        <Route path={routes.TERMS_OF_USE()} element={<TermsOfUse />} />
        <Route
          path={routes.STANDARD_CONTRACT()}
          element={<StandardContract />}
        />
        <Route path={routes.CONTACT_US()} element={<ContactUs />} />
        <Route path={routes.ABOUT_US()} element={<AboutUs />} />
        <Route path={routes.COOKIE_POLICY()} element={<CookiePolicy />} />
        <Route
          path={routes.CONTENT_REQUEST_TERMS()}
          element={<TermsOfRequests />}
        />
        <Route path={routes.SIGN_IN()} element={<Landing type="sign-in" />} />
        <Route path={`${routes.CREATOR_PROFILE()}me`} element={<NotFound />} />
        <Route
          path={routes.MY_PROFILE()}
          element={<CreatorProfile shouldRedirect />}
        />
        <Route
          path={`${routes.CREATOR_PROFILE()}:username`}
          element={<CreatorProfile />}
        />
        <Route path={`${routes.STREAM()}/:uniqueKey`} element={<Audience />} />
        <Route
          path={`${routes.STREAM()}/:uniqueKey/scheduled`}
          element={<Host />}
        />
        <Route path={routes.STREAM()} element={<Host />} />
        <Route
          path={`${routes.CONTENT_REQUEST_COMPLETE_PAYMENT()}/:username`}
          element={<ContentRequestCompletePayment />}
        />
        <Route
          path={`${routes.CONTENT_REQUEST_COMPLETE_PAYMENT_SUCCESS()}/:id`}
          element={<ContentRequestCompletePaymentSuccess />}
        />
        <Route
          path={`${routes.CONTENT_REQUEST_COMPLETE_PAYMENT_SUCCESS()}`}
          element={<ContentRequestCompletePaymentSuccess />}
        />
        <Route path={`${routes.CREATORS()}:ref`} element={<Landing />} />
        <Route path="/" element={<Landing />} exact />
        <Route path="*" element={<Navigate to="/" />} />
      </Route>,
    ),
  );
};

export default useAppRoutes;
